import { Avatar, InputBar, ListItem, ListTemplate, SearchBar, Stack } from '@room/shared-ui';
import { Button } from '@room/shared-ui/components/ui/button';
import type { ChatSummary } from '../types';

export type ChatListPanelProps = {
  filteredChats: ChatSummary[];
  isLoading: boolean;
  searchQuery: string;
  onSearch: (value: string) => void;
  selectedChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onCreateGroup?: () => void;
  getChatDisplayName: (chat: ChatSummary) => string;
  formatChatTimestamp: (chat: ChatSummary) => string | null;
  showMobileMessages: boolean;
  totalChats: number;
};

export function ChatListPanel({
  filteredChats,
  isLoading,
  searchQuery,
  onSearch,
  selectedChatId,
  onSelectChat,
  onCreateGroup,
  getChatDisplayName,
  formatChatTimestamp,
  showMobileMessages,
  totalChats,
}: ChatListPanelProps) {
  const hasChats = totalChats > 0;
  return (
    <Stack
      header={(
        <InputBar
          border="bottom"
          input={(
            <SearchBar
              placeholder="Search chats..."
              value={searchQuery}
              onSearch={onSearch}
              debounce={300}
              fullWidth
              className="chat-search-input"
            />
          )}
          action={(
            <Button
              variant="ghost"
              size="sm"
              title="Create new group"
              aria-label="Create new group chat"
              onClick={onCreateGroup}
              disabled={!onCreateGroup}
              icon={<span>✏️</span>}
            />
          )}
        />
      )}
    >
      <ListTemplate
        items={filteredChats}
        loading={isLoading}
        emptyState={
          !hasChats ? (
            <div className="empty-state">
              <div className="empty-icon">💬</div>
              <p>No chats yet.</p>
              <p className="empty-state-text">Start a new conversation from admin.</p>
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <p>No chats found</p>
              <p className="empty-state-text">Try a different search term</p>
            </div>
          )
        }
        renderItem={(chat) => (
          <ListItem
            leading={(
              <Avatar
                fallback={getChatDisplayName(chat)[0]?.toUpperCase() ?? 'C'}
                size="base"
              />
            )}
            title={getChatDisplayName(chat)}
            subtitle={(
              <span className="general-list-preview">
                {chat.lastMessage?.content || 'No messages yet'}
              </span>
            )}
            trailing={formatChatTimestamp(chat)}
            active={chat.id === selectedChatId}
            primaryAction={{
              onClick: () => onSelectChat(chat.id),
              ariaLabel: `Open chat ${getChatDisplayName(chat)}`,
            }}
          />
        )}
        keyExtractor={(chat) => chat.id}
        listClassName="general-list"
        variant="compact"
      />
    </Stack>
  );
}
